import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  computed,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  CurrentScopeFacade,
  ManagementService,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { FeatureFlagsService } from '@principle-theorem/ng-feature-flags';
import { type IBreadcrumb } from '@principle-theorem/ng-shared';
import {
  IBrand,
  IPractice,
  Permission,
} from '@principle-theorem/principle-core/interfaces';
import { User } from '@principle-theorem/principle-core';
import { WithRef } from '@principle-theorem/shared';
import { isArray } from 'lodash';
import {
  BrandPermissions,
  DEPOSITS_FEATURE,
  OrganisationPermissions,
  PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE,
  PracticePermissions,
} from '@principle-theorem/principle-core/features';
import { map } from 'rxjs/operators';

interface ISettingsLink extends IBreadcrumb {
  permissions?: Permission[];
  hidden?: boolean;
}

interface ISettingsGroup {
  label: string;
  children: ISettingsLink[];
}

@Component({
  selector: 'pr-settings-splash',
  templateUrl: './settings-splash.component.html',
  styleUrls: ['./settings-splash.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SettingsSplashComponent {
  breadcrumbs: IBreadcrumb[] = [{ label: 'Settings' }];
  settingsGroups: Signal<ISettingsGroup[]>;
  userPermissions: Signal<string[]>;
  isManagementUser: Signal<boolean>;

  constructor(
    currentScope: CurrentScopeFacade,
    private _organisation: OrganisationService,
    private _management: ManagementService,
    featureFlags: FeatureFlagsService
  ) {
    const brand = toSignal(currentScope.currentBrand$);
    const practice = toSignal(currentScope.currentPractice$);
    this.userPermissions = toSignal(this._organisation.userPermissions$, {
      initialValue: [],
    });
    this.isManagementUser = toSignal(
      this._management.user$.pipe(map((user) => !!user)),
      { initialValue: false }
    );
    const practiceStafferFeeSchedulesEnabled = toSignal(
      featureFlags.isFeatureEnabled$(PRACTICE_STAFFER_FEE_SCHEDULES_FEATURE),
      { initialValue: false }
    );
    const depositsEnabled = toSignal(
      featureFlags.isFeatureEnabled$(DEPOSITS_FEATURE),
      { initialValue: false }
    );

    this.settingsGroups = computed(() => {
      const allGroups = this._getNavGroups(
        practiceStafferFeeSchedulesEnabled(),
        depositsEnabled(),
        brand(),
        practice()
      );
      return this._filterGroupsByPermission(allGroups);
    });
  }

  private _filterGroupsByPermission(
    groups: ISettingsGroup[]
  ): ISettingsGroup[] {
    const isManagementUser = this.isManagementUser();
    const userPermissions = this.userPermissions();

    return groups
      .map((group) => ({
        ...group,
        children: group.children.filter((link) => {
          if (link.hidden) {
            return false;
          }

          if (
            !link.permissions ||
            !link.permissions.length ||
            isManagementUser
          ) {
            return true;
          }

          return User.hasPermissions(link.permissions, userPermissions);
        }),
      }))
      .filter((group) => group.children.length > 0);
  }

  private _getNavGroups(
    practiceStafferFeeSchedulesEnabled: boolean,
    depositsEnabled: boolean,
    brand?: WithRef<IBrand>,
    practice?: WithRef<IPractice>
  ): ISettingsGroup[] {
    const brandUid = brand?.ref.id;
    const practiceUid = practice?.ref.id;

    return this._removeEmptyGroupsAndNonLinks([
      {
        label: 'Brand Settings',
        children: [
          {
            label: 'General',
            path: this._brandPath(brandUid, 'general'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Practices',
            path: this._brandPath(brandUid, 'practices'),
            permissions: [
              BrandPermissions.BrandConfigure,
              PracticePermissions.PracticeManage,
            ],
          },
        ],
      },
      {
        label: 'Practice Settings',
        children: [
          {
            label: 'General',
            path: this._practicePath(practiceUid, 'general'),
            permissions: [PracticePermissions.PracticeConfigure],
          },
          {
            label: 'Opening Hours',
            path: this._practicePath(practiceUid, 'opening-hours'),
            permissions: [PracticePermissions.PracticeConfigure],
          },
          {
            label: 'Patient Portal',
            path: this._practicePath(practiceUid, 'patient-portal'),
            permissions: [PracticePermissions.PracticeConfigure],
          },
        ],
      },
      {
        label: 'My Settings',
        children: [
          {
            label: 'General',
            path: this._mySettingsPath(brandUid, 'general'),
            permissions: [], // Everyone can access their own settings
          },
        ],
      },
      {
        label: 'Accounting',
        children: [
          {
            label: 'Workspace Fee Schedules',
            path: this._workspacePath('fee-schedules'),
            permissions: [
              OrganisationPermissions.OrganisationConfigureFeeSchedules,
            ],
          },
          {
            label: 'Practice Fee Schedules',
            path: this._practicePath(practiceUid, 'fee-schedules'),
            permissions: [PracticePermissions.PracticeConfigure],
            hidden: !practiceStafferFeeSchedulesEnabled,
          },
          {
            label: 'My Fee Schedules',
            path: this._mySettingsPath(brandUid, 'fee-schedules'),
            permissions: [], // Everyone can access their own fee schedules
            hidden: !practiceStafferFeeSchedulesEnabled,
          },
          {
            label: 'Transaction Types, Invoicing & Practitioner Income',
            path: this._brandPath(brandUid, 'accounting'),
            permissions: [BrandPermissions.BrandConfigure],
          },
          {
            label: 'Deposit Settings',
            path: this._brandPath(brandUid, 'deposit-settings'),
            hidden: !depositsEnabled,
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Products',
            path: this._brandPath(brandUid, 'products'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureProducts,
            ],
          },
        ],
      },
      {
        label: 'Users',
        children: [
          {
            label: 'Users',
            path: this._workspacePath('users'),
            permissions: [OrganisationPermissions.OrganisationConfigureAuth],
          },
          {
            label: 'Staff',
            path: this._brandPath(brandUid, 'staff'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureStaff,
            ],
          },
          {
            label: 'Roles',
            path: this._workspacePath('roles'),
            permissions: [OrganisationPermissions.OrganisationConfigureAuth],
          },
          {
            label: 'Security',
            path: this._workspacePath('security'),
            permissions: [OrganisationPermissions.OrganisationConfigureAuth],
          },
          {
            label: 'Teams',
            path: this._brandPath(brandUid, 'teams'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureTeams,
            ],
          },
        ],
      },
      {
        label: 'Integrations',
        children: [
          {
            label: 'Workspace Integrations',
            path: this._workspacePath('integrations'),
            permissions: [
              OrganisationPermissions.OrganisationConfigureIntegrations,
            ],
          },
          {
            label: 'Practice Integrations',
            path: this._practicePath(practiceUid, 'integrations'),
            permissions: [PracticePermissions.PracticeConfigure],
          },
        ],
      },
      {
        label: 'Treatments & Conditions',
        children: [
          {
            label: 'Condition Configurations',
            path: this._brandPath(brandUid, ['charting', 'conditions']),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureClinical,
            ],
          },
          {
            label: 'Treatment Configurations',
            path: this._brandPath(brandUid, ['charting', 'treatments']),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureClinical,
            ],
          },
          {
            label: 'Multi-Treatment Configurations',
            path: this._brandPath(brandUid, ['charting', 'multi-treatments']),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureClinical,
            ],
          },
          {
            label: 'Treatment Templates',
            path: this._brandPath(brandUid, 'treatment-templates'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureClinical,
            ],
          },
          {
            label: 'Treatment Categories',
            path: this._brandPath(brandUid, 'treatment-categories'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureClinical,
            ],
          },
        ],
      },
      {
        label: 'Miscellaneous',
        children: [
          {
            label: 'Automation Configurations',
            path: this._brandPath(brandUid, 'automations'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureAutomations,
            ],
          },
          {
            label: 'Rescheduling & Cancellation Reasons',
            path: this._brandPath(brandUid, 'cancellation-reasons'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Document Templates',
            path: this._brandPath(brandUid, 'document-templates'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Media Tags',
            path: this._practicePath(practiceUid, 'tags'),
            permissions: [
              BrandPermissions.TagsManage,
              PracticePermissions.PracticeConfigure,
            ],
          },
          {
            label: 'Medical History Alerts',
            path: this._brandPath(brandUid, 'medical-history'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'My Snippets',
            path: this._mySettingsPath(brandUid, 'snippets'),
            permissions: [], // Everyone can access their own snippets
          },
          {
            label: 'Prescriptions',
            path: this._brandPath(brandUid, 'prescriptions'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigurePrescriptions,
            ],
          },
          {
            label: 'Referral Sources',
            path: this._brandPath(brandUid, 'referral-sources'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Shared Snippets',
            path: this._brandPath(brandUid, 'snippets'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.BrandConfigureGeneral,
            ],
          },
          {
            label: 'Sterilisation',
            path: this._brandPath(brandUid, 'sterilisation'),
            permissions: [BrandPermissions.BrandConfigure],
          },
          {
            label: 'Tags',
            path: this._brandPath(brandUid, 'tags'),
            permissions: [
              BrandPermissions.BrandConfigure,
              BrandPermissions.TagsManage,
            ],
          },
          {
            label: 'Custom Forms',
            path: this._brandPath(brandUid, 'custom-forms'),
            permissions: [BrandPermissions.BrandConfigure],
          },
        ],
      },
    ]);
  }

  private _workspacePath(path: string[] | string): string[] {
    const segments = isArray(path) ? path : [path];
    return ['workspace', ...segments];
  }

  private _brandPath(
    brandUid: string | undefined,
    path: string[] | string
  ): string[] | undefined {
    return this._path('brand', brandUid, path);
  }

  private _practicePath(
    practiceUid: string | undefined,
    path: string[] | string
  ): string[] | undefined {
    return this._path('practice', practiceUid, path);
  }

  private _mySettingsPath(
    brandUid: string | undefined,
    path: string[] | string
  ): string[] | undefined {
    const segments = isArray(path) ? path : [path];
    return this._brandPath(brandUid, ['account', ...segments]);
  }

  private _path(
    root: string,
    uid: string | undefined,
    path: string[] | string
  ): string[] | undefined {
    if (!uid) {
      return;
    }
    const segments = isArray(path) ? path : [path];
    return [root, uid, ...segments];
  }

  private _removeEmptyGroupsAndNonLinks(
    groups: ISettingsGroup[]
  ): ISettingsGroup[] {
    return groups
      .map((group) => ({
        ...group,
        children: group.children.filter((link) => !!link.path),
      }))
      .filter((group) => group.children.length > 0);
  }
}
